import React from 'react';
import { CheckSquare, Square, RotateCcw, Info, Briefcase, User, Truck, Settings } from 'lucide-react';

const ROLE_TEMPLATE_META = {
  sales_rep: { icon: Briefcase, label: 'Sales Rep', color: 'text-blue-600' },
  cashier: { icon: User, label: 'Cashier / POS', color: 'text-purple-600' },
  delivery: { icon: Truck, label: 'Delivery', color: 'text-amber-600' },
  custom: { icon: Settings, label: 'Custom', color: 'text-slate-600' }
};

const AccessControlsTab = ({ formData, setFormData, roleTemplates = [] }) => {
  const modules = [
    {
      category: 'CATALOGUE',
      items: [
        { key: 'customers', label: 'Customers', actions: ['view', 'create', 'edit', 'delete', 'export'] },
        { key: 'suppliers', label: 'Suppliers', actions: ['view', 'create', 'edit', 'delete', 'export'] },
        { key: 'products', label: 'Products', actions: ['view', 'create', 'edit', 'delete', 'export'] },
      ]
    },
    {
      category: 'SALES',
      items: [
        { key: 'quotations', label: 'Quotations', actions: ['view', 'create', 'edit', 'delete', 'export'] },
        { key: 'sales_orders', label: 'Sales Orders', actions: ['view', 'create', 'edit', 'delete', 'export', 'approve'] },
        { key: 'proforma_invoices', label: 'Proforma Invoices', actions: ['view', 'create', 'edit', 'delete', 'export'] },
        { key: 'delivery_challans', label: 'Delivery Challans', actions: ['view', 'create', 'edit', 'delete', 'export'] },
        { key: 'invoices', label: 'Sales Invoices', actions: ['view', 'create', 'edit', 'delete', 'export', 'approve'] },
        { key: 'sales_returns', label: 'Sales Returns', actions: ['view', 'create', 'edit', 'delete', 'export'] },
        { key: 'payments_received', label: 'Payments Received', actions: ['view', 'create', 'edit', 'delete', 'export'] },
      ]
    },
    {
      category: 'POS',
      items: [
        { key: 'pos', label: 'POS / Quick Sale', actions: ['view', 'create', 'print'] },
      ]
    },
    {
      category: 'COLLECTIONS & OUTSTANDING',
      items: [
        { key: 'collections', label: 'Collections', actions: ['view', 'create', 'edit', 'delete', 'export'] },
        { key: 'outstanding', label: 'Outstanding', actions: ['view'] },
      ]
    },
    {
      category: 'PURCHASES (RESTRICTED)',
      items: [
        { key: 'purchase_orders', label: 'Purchase Orders', actions: ['view', 'create', 'edit', 'delete', 'export'] },
        { key: 'purchase_bills', label: 'Purchase Bills', actions: ['view', 'create', 'edit', 'delete', 'export'] },
        { key: 'payments_made', label: 'Payments Made', actions: ['view', 'create', 'edit', 'delete', 'export'] },
      ]
    },
    {
      category: 'FIELD TRACKING',
      items: [
        { key: 'attendance', label: 'Attendance', actions: ['view', 'create'] },
        { key: 'visits', label: 'Visits & Tasks', actions: ['view', 'create', 'edit'] },
        { key: 'retailer_map', label: 'Retailer Map', actions: ['view'] },
        { key: 'placement', label: 'Product Placement', actions: ['view', 'create', 'edit', 'delete', 'export'] },
      ]
    },
    {
      category: 'STAFF SELF-SERVICE (ALWAYS ENABLED)',
      items: [
        { key: 'my_tracking', label: 'My Tracking', actions: ['view'] },
        { key: 'my_performance', label: 'My Performance', actions: ['view'] },
        { key: 'my_profile', label: 'My Profile', actions: ['view', 'edit'] },
      ]
    },
  ];

  const actionLabels = {
    view: 'View',
    create: 'Create',
    edit: 'Edit',
    delete: 'Delete',
    export: 'Export',
    approve: 'Approve',
    print: 'Print'
  };

  const getPermissionValue = (moduleKey, action) => {
    return formData.permissions?.[moduleKey]?.[action] || false;
  };

  const setPermissionValue = (moduleKey, action, value) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [moduleKey]: {
          ...(prev.permissions?.[moduleKey] || {}),
          [action]: value
        }
      },
      // Mark as custom if manually changing
      role_template: 'custom'
    }));
  };

  const toggleModuleAll = (moduleKey, actions) => {
    const allChecked = actions.every(action => getPermissionValue(moduleKey, action));
    const newValue = !allChecked;
    
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [moduleKey]: actions.reduce((acc, action) => ({
          ...acc,
          [action]: newValue
        }), {})
      },
      role_template: 'custom'
    }));
  };

  const toggleActionColumn = (action) => {
    const allModules = modules.flatMap(cat => cat.items);
    const allChecked = allModules.every(module => 
      !module.actions.includes(action) || getPermissionValue(module.key, action)
    );
    const newValue = !allChecked;

    const updatedPermissions = { ...formData.permissions };
    allModules.forEach(module => {
      if (module.actions.includes(action)) {
        updatedPermissions[module.key] = {
          ...(updatedPermissions[module.key] || {}),
          [action]: newValue
        };
      }
    });

    setFormData(prev => ({
      ...prev,
      permissions: updatedPermissions,
      role_template: 'custom'
    }));
  };

  const resetToTemplate = (templateId) => {
    const template = roleTemplates.find(t => t.id === templateId);
    if (template) {
      setFormData(prev => ({
        ...prev,
        role_template: templateId,
        permissions: template.permissions
      }));
    }
  };

  const currentTemplateMeta = ROLE_TEMPLATE_META[formData.role_template] || ROLE_TEMPLATE_META.custom;
  const CurrentIcon = currentTemplateMeta.icon;

  return (
    <div className="space-y-4">
      {/* Current Template Banner */}
      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-full bg-white border flex items-center justify-center ${currentTemplateMeta.color}`}>
            <CurrentIcon className="w-4 h-4" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-700">
              Based on: <span className={currentTemplateMeta.color}>{currentTemplateMeta.label}</span>
              {formData.role_template === 'custom' && (
                <span className="ml-2 text-xs text-amber-600">(Modified)</span>
              )}
            </p>
            <p className="text-xs text-slate-500">
              Customize below or reset to a template
            </p>
          </div>
        </div>
        
        {/* Reset to Template Dropdown */}
        <div className="relative group">
          <button
            type="button"
            className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-teal-600 hover:text-teal-700 hover:bg-teal-50 rounded-lg transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Reset to Template
          </button>
          <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-slate-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
            {roleTemplates.map((template) => {
              const meta = ROLE_TEMPLATE_META[template.id] || ROLE_TEMPLATE_META.custom;
              const Icon = meta.icon;
              return (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => resetToTemplate(template.id)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 first:rounded-t-lg last:rounded-b-lg"
                >
                  <Icon className={`w-4 h-4 ${meta.color}`} />
                  {template.name}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-blue-800">
          <strong>Tip:</strong> Staff can only access modules with "View" permission. 
          Purchases and Settings are typically restricted to owners.
        </p>
      </div>

      {/* Permissions Table */}
      <div className="overflow-x-auto border rounded-lg">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-slate-100 border-b-2 border-slate-200">
              <th className="text-left py-2.5 px-3 font-semibold text-slate-700 w-48">
                Module
              </th>
              {['view', 'create', 'edit', 'delete', 'export'].map(action => (
                <th key={action} className="text-center py-2.5 px-1.5 font-semibold text-slate-700 w-16">
                  <div className="flex flex-col items-center gap-0.5">
                    <span className="text-xs">{actionLabels[action]}</span>
                    <button
                      type="button"
                      onClick={() => toggleActionColumn(action)}
                      className="text-[10px] text-teal-600 hover:text-teal-700 font-normal"
                    >
                      All
                    </button>
                  </div>
                </th>
              ))}
              <th className="text-center py-2.5 px-2 font-semibold text-slate-700 w-16">
                <span className="text-xs">Toggle</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {modules.map((category, catIndex) => (
              <React.Fragment key={catIndex}>
                {/* Category Header */}
                <tr className="bg-slate-50">
                  <td colSpan={7} className="py-2 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                    {category.category}
                  </td>
                </tr>
                
                {/* Module Rows */}
                {category.items.map((module, modIndex) => (
                  <tr 
                    key={module.key}
                    className={`border-b border-slate-100 hover:bg-teal-50/30 transition-colors ${modIndex % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}
                  >
                    <td className="py-2 px-3 text-slate-700 font-medium">
                      {module.label}
                    </td>
                    
                    {['view', 'create', 'edit', 'delete', 'export'].map(action => (
                      <td key={action} className="text-center py-2 px-1.5">
                        {module.actions.includes(action) ? (
                          <button
                            type="button"
                            onClick={() => setPermissionValue(module.key, action, !getPermissionValue(module.key, action))}
                            className="inline-flex items-center justify-center w-6 h-6 rounded hover:bg-slate-100 transition-colors"
                            data-testid={`perm-${module.key}-${action}`}
                          >
                            {getPermissionValue(module.key, action) ? (
                              <CheckSquare className="w-4.5 h-4.5 text-teal-600" />
                            ) : (
                              <Square className="w-4.5 h-4.5 text-slate-300" />
                            )}
                          </button>
                        ) : (
                          <span className="text-slate-200">—</span>
                        )}
                      </td>
                    ))}
                    
                    {/* Toggle Row */}
                    <td className="text-center py-2 px-2">
                      <button
                        type="button"
                        onClick={() => toggleModuleAll(module.key, module.actions)}
                        className="text-[10px] text-teal-600 hover:text-teal-700 font-medium px-2 py-0.5 hover:bg-teal-50 rounded"
                      >
                        All
                      </button>
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Warning for Restricted Modules */}
      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <p className="text-xs text-amber-800">
          <strong>Note:</strong> Purchase modules, Finance, and Settings access is typically restricted to business owners. 
          Staff self-service modules (My Performance, My Profile) are always enabled.
        </p>
      </div>
    </div>
  );
};

export default AccessControlsTab;
